/**
 * File utility functions for handling file operations and MIME type normalization
 */

/**
 * Normalize MIME types for uploaded files to ensure compatibility with Supabase Storage
 * 
 * @param file - The original File object
 * @returns A new File object with normalized MIME type, or the original file if no normalization needed
 */
export function normalizeFileForUpload(file: File): File {
  console.log('🔍 FileUtils: Normalizing file MIME type', {
    name: file.name,
    originalType: file.type,
    size: file.size
  });

  // Check if it's a CSV file with an Excel MIME type
  const isCSVFile = file.name.toLowerCase().endsWith('.csv')
  const hasExcelMimeType = file.type === 'application/vnd.ms-excel' || 
                          file.type === 'application/msexcel' ||
                          file.type === 'application/x-msexcel'
  
  if (isCSVFile && (hasExcelMimeType || !file.type)) {
    console.log('📊 FileUtils: Converting CSV file MIME type', { from: file.type, to: 'text/csv' });
    return new File([file], file.name, {
      type: 'text/csv',
      lastModified: file.lastModified
    })
  }
  
  // Check if it's a LAS file with octet-stream or no MIME type
  const isLASFile = file.name.toLowerCase().endsWith('.las')
  const hasOctetStreamType = file.type === 'application/octet-stream' || !file.type
  
  if (isLASFile && hasOctetStreamType) {
    console.log('🏗️ FileUtils: Converting LAS file MIME type', { 
      from: file.type, 
      to: 'text/plain',
      fileName: file.name 
    });
    return new File([file], file.name, {
      type: 'text/plain',
      lastModified: file.lastModified
    })
  }
  
  console.log('✅ FileUtils: No MIME type conversion needed', { 
    fileName: file.name, 
    mimeType: file.type 
  });
  return file
}

/**
 * Normalize multiple files for upload
 * 
 * @param files - Array of File objects to normalize
 * @returns Array of normalized File objects
 */
export function normalizeFilesForUpload(files: File[]): File[] {
  const normalizedFiles = files.map(normalizeFileForUpload);
  console.log('📤 FileUtils: Normalized multiple files', {
    originalCount: files.length,
    normalizedCount: normalizedFiles.length
  });
  return normalizedFiles;
}